import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import countries from 'world-countries';

interface AsianCity {
  name: string;
  country: string;
}

/** PNG width for small inline flag chips (~32px display at 2x). */
const FLAGCDN_CHIP_WIDTH = 160;

/**
 * Resolves country flags for team names from registration:
 * - Country names from `world-countries` (ISO alpha-2)
 * - Asian capital cities (same as team registration)
 * - Asian cities from `assets/asiancities.json`
 * Keys are matched case-insensitively.
 * Images: https://flagcdn.com
 */
@Injectable({ providedIn: 'root' })
export class TeamFlagService {
  private readonly nameToCca2 = new Map<string, string>();

  constructor(private http: HttpClient) {
    this.buildCountryAndAsianCapitalMaps();
    this.http
      .get<AsianCity[]>('assets/asiancities.json')
      .pipe(catchError(() => of([] as AsianCity[])))
      .subscribe((cities) => {
        for (const city of cities) {
          if (city.country) {
            this.setNameCca2(city.name, city.country);
          }
        }
      });
  }

  /** PNG for small inline images (avatar chips, thumbnails). */
  getFlagUrl(teamName: string | null | undefined): string | null {
    const code = this.getCountryCode(teamName);
    if (!code) {
      return null;
    }
    return `https://flagcdn.com/w${FLAGCDN_CHIP_WIDTH}/${code.toLowerCase()}.png`;
  }

  /** SVG for large CSS backgrounds — stays sharp at any size. */
  getFlagBackgroundUrl(teamName: string | null | undefined): string | null {
    const code = this.getCountryCode(teamName);
    if (!code) {
      return null;
    }
    return `https://flagcdn.com/${code.toLowerCase()}.svg`;
  }

  /** ISO 3166-1 alpha-2, or null. */
  getCountryCode(teamName: string | null | undefined): string | null {
    if (!teamName?.trim()) {
      return null;
    }
    const key = teamName.trim().toLowerCase();
    return this.nameToCca2.get(key) ?? null;
  }

  /** Emoji fallback for places where remote flag images fail to load. */
  getFlagEmoji(teamName: string | null | undefined): string | null {
    const code = this.getCountryCode(teamName);
    if (!code || code.length !== 2) {
      return null;
    }

    const points = code
      .toUpperCase()
      .split('')
      .map((char) => 0x1f1e6 + char.charCodeAt(0) - 65);
    return String.fromCodePoint(...points);
  }

  private setNameCca2(name: string, cca2: string) {
    if (!name?.trim() || !cca2) {
      return;
    }
    this.nameToCca2.set(name.trim().toLowerCase(), cca2.toUpperCase());
  }

  private buildCountryAndAsianCapitalMaps() {
    for (const c of countries) {
      this.setNameCca2(c.name.common, c.cca2);
    }
    for (const c of countries) {
      if (c.region === 'Asia' && c.capital?.length) {
        this.setNameCca2(c.capital[0], c.cca2);
      }
    }
  }
}
